import { useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { CloudOff, Wifi } from 'lucide-react';
import { useEvent } from '../context/EventContext';
import { shouldReturnToTopicList } from '../lib/presenterFollow';
import { NowPresenting } from './NowPresenting';

export function AppShell({ children }: { children: React.ReactNode }) {
  const {
    snapshot,
    online,
    demoMode,
    followPresenter,
    setFollowPresenter,
  } = useEvent();
  const location = useLocation();
  const navigate = useNavigate();
  const previousBrowsingEnabled = useRef<boolean | null>(null);
  const previousGeneration = useRef<number | null>(null);

  useEffect(() => {
    const generation = snapshot?.settings.interactionGeneration;
    if (generation === undefined) return;
    const wasReset = previousGeneration.current !== null &&
      previousGeneration.current !== generation;
    previousGeneration.current = generation;
    if (wasReset && location.pathname !== '/presenter') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, snapshot?.settings.interactionGeneration]);

  useEffect(() => {
    const nextBrowsingEnabled = snapshot?.settings.audienceBrowsingEnabled;
    if (typeof nextBrowsingEnabled !== 'boolean') return;
    const shouldReturn = shouldReturnToTopicList(
      previousBrowsingEnabled.current,
      nextBrowsingEnabled,
    );
    previousBrowsingEnabled.current = nextBrowsingEnabled;
    if (shouldReturn && location.pathname !== '/presenter') {
      navigate('/', { replace: true });
    }
  }, [
    location.pathname,
    navigate,
    snapshot?.settings.audienceBrowsingEnabled,
  ]);

  if (location.pathname === '/presenter') {
    return <div className="presenter-shell">{children}</div>;
  }
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link className="brand" to="/" aria-label="Panther Meeting home">
          <img className="brand-logo" src="/signify-logo.svg" alt="Signify" />
          <span>
            <strong>{snapshot?.settings.eventTitle ?? 'Panther Meeting 2026'}</strong>
            <small>Audience experience</small>
          </span>
        </Link>
        <div className="topbar-status" aria-live="polite">
          {demoMode && <span className="demo-chip">Demo data</span>}
          <span className={online ? 'online' : 'offline'}>
            {online ? <Wifi size={16} /> : <CloudOff size={16} />}
            {online ? 'Connected' : 'Offline'}
          </span>
        </div>
      </header>
      <div className="follow-strip">
        <label className="switch-control">
          <input
            type="checkbox"
            checked={followPresenter}
            onChange={(event) => setFollowPresenter(event.target.checked)}
          />
          <span aria-hidden="true" />
          Follow presentation
        </label>
        {!snapshot?.settings.audienceFollowEnabled && (
          <small>Presenter follow is paused.</small>
        )}
      </div>
      <NowPresenting key={`follow-${snapshot?.settings.interactionGeneration}`} />
      <main id="main-content" key={`audience-${snapshot?.settings.interactionGeneration}`}>{children}</main>
      {snapshot?.settings.audienceBrowsingEnabled && (
        <nav className="bottom-nav" aria-label="Primary navigation">
          <NavLink to="/" end>
            Topics
          </NavLink>
          <NavLink to="/leaderboard">Leaderboard</NavLink>
        </nav>
      )}
    </div>
  );
}
